import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import {
    ShieldAlert, Landmark, Users,
    AlertCircle, HelpCircle, Info,
    ChevronDown, CheckCircle, Clock,
    Edit2, Save
} from "lucide-react";

const PoliticalForm = ({ formData, preview, handleChange, loading, errors, setPreview, onSave }) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (formData.supportedPartyYesNo && !formData.supportedPartyName?.trim()) {
            newErrors.supportedPartyName = "Party name is required";
        }
        if (formData.farmerOrgYesNo && !formData.farmerOrganisation?.trim()) {
            newErrors.farmerOrganisation = "Organisation name is required";
        }
        if (formData.proKhalistanYesNo && !formData.proKhalistanSupport?.trim()) {
            newErrors.proKhalistanSupport = "Organisation details are required";
        }
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview && !isEditing) {
        return (
            <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <ShieldAlert size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Affiliation Declaration</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Mandatory disclosure for international processing</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Affiliation declaration verified and approved</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                        <PreviewCard
                            label="Political Support"
                            value={preview.supportedPartyYesNo ? "Yes" : "No"}
                            subLabel="Party Name"
                            subValue={preview.supportedPartyName}
                            icon={<Landmark size={18} />}
                            isActive={preview.supportedPartyYesNo}
                        />
                        <PreviewCard
                            label="Farmer Organisation"
                            value={preview.farmerOrgYesNo ? "Yes" : "No"}
                            subLabel="Org Name"
                            subValue={preview.farmerOrganisation}
                            icon={<Users size={18} />}
                            isActive={preview.farmerOrgYesNo}
                        />
                        <div className="md:col-span-2">
                            <PreviewCard
                                label="Security Declaration"
                                value={preview.proKhalistanYesNo ? "Yes" : "No"}
                                subLabel="Organisation Detail"
                                subValue={preview.proKhalistanSupport}
                                icon={<ShieldAlert size={18} />}
                                isActive={preview.proKhalistanYesNo}
                                fullWidth
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Processing declaration...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <ShieldAlert size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Affiliation Declaration</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Mandatory disclosure for international processing</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                            {errors?.map((err, i) => (
                                <li key={`err-${i}`} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    <div className="space-y-6 sm:space-y-8">
                        <QuestionBlock
                            icon={<Landmark size={20} />}
                            label="Political Support"
                            question="Do you support any political party?"
                            yesValue={formData.supportedPartyYesNo}
                            onRadioChange={(val) => handleChange("supportedPartyYesNo", val)}
                            isExpanded={expandedSections.politicalSupport}
                            onToggle={() => toggleSection('politicalSupport')}
                        >
                            {formData.supportedPartyYesNo && (
                                <Input
                                    label="Enter Full Party Name"
                                    value={formData.supportedPartyName || ""}
                                    onChange={(v) => handleChange("supportedPartyName", v)}
                                    error={localErrors.supportedPartyName}
                                    placeholder="e.g., Indian National Congress, BJP"
                                />
                            )}
                        </QuestionBlock>

                        <QuestionBlock
                            icon={<Users size={20} />}
                            label="Farmers Association"
                            question="Do you support any farmer organisation?"
                            yesValue={formData.farmerOrgYesNo}
                            onRadioChange={(val) => handleChange("farmerOrgYesNo", val)}
                            isExpanded={expandedSections.farmerOrg}
                            onToggle={() => toggleSection('farmerOrg')}
                        >
                            {formData.farmerOrgYesNo && (
                                <Input
                                    label="Enter Organisation Name"
                                    value={formData.farmerOrganisation || ""}
                                    onChange={(v) => handleChange("farmerOrganisation", v)}
                                    error={localErrors.farmerOrganisation}
                                    placeholder="e.g., Bharatiya Kisan Union"
                                />
                            )}
                        </QuestionBlock>

                        <QuestionBlock
                            icon={<ShieldAlert size={20} />}
                            label="Security Declaration"
                            question="Do you support any Pro Khalistan organisation?"
                            yesValue={formData.proKhalistanYesNo}
                            onRadioChange={(val) => handleChange("proKhalistanYesNo", val)}
                            isWarning
                            isExpanded={expandedSections.proKhalistan}
                            onToggle={() => toggleSection('proKhalistan')}
                        >
                            {formData.proKhalistanYesNo && (
                                <Input
                                    label="Provide Details of Organisation"
                                    value={formData.proKhalistanSupport || ""}
                                    onChange={(v) => handleChange("proKhalistanSupport", v)}
                                    error={localErrors.proKhalistanSupport}
                                    placeholder="Please provide complete details"
                                    isWarning
                                />
                            )}
                        </QuestionBlock>
                    </div>

                </form>
            </div>
        </div>
    );
};

/* REUSABLE COMPONENTS */
const QuestionBlock = ({ icon, label, question, yesValue, onRadioChange, children, isWarning, isExpanded, onToggle }) => (
    <div className={`bg-white border rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all
        ${yesValue ? (isWarning ? 'border-rose-200' : 'border-indigo-200') : 'border-gray-200'}`}>

        {/* Question Header - Clickable on mobile */}
        <div
            className={`p-4 sm:p-5 cursor-pointer md:cursor-default transition-colors
                ${yesValue ? (isWarning ? 'bg-rose-50/50' : 'bg-indigo-50/30') : 'bg-gray-50/50'}`}
            onClick={onToggle}
        >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg shrink-0 ${isWarning ? 'bg-rose-100 text-rose-600' : 'bg-gray-100 text-gray-600'}`}>
                        {icon}
                    </div>
                    <div>
                        <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wider ${isWarning ? 'text-rose-500' : 'text-gray-500'}`}>
                            {label}
                        </span>
                        <p className="text-sm sm:text-base font-semibold text-gray-800 leading-tight">{question}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex bg-gray-200 p-1 rounded-lg w-fit">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRadioChange(true);
                            }}
                            className={`px-4 sm:px-5 py-1.5 rounded-md text-xs sm:text-sm font-bold transition-all 
                                ${yesValue === true ? (isWarning ? 'bg-rose-500 text-white shadow-md' : 'bg-indigo-600 text-white shadow-md') : 'text-gray-500'}`}
                        >
                            YES
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRadioChange(false);
                            }}
                            className={`px-4 sm:px-5 py-1.5 rounded-md text-xs sm:text-sm font-bold transition-all 
                                ${yesValue === false ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}
                        >
                            NO
                        </button>
                    </div>
                    <button className="md:hidden text-gray-400">
                        <ChevronDown size={18} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                </div>
            </div>
        </div>

        {/* Details Section - Expandable */}
        {yesValue && (
            <div className={`
                transition-all duration-300 overflow-hidden
                ${isExpanded !== false ? 'max-h-[500px] opacity-100' : 'max-h-0 md:max-h-[500px] md:opacity-100'}
            `}>
                <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                    {children}
                </div>
            </div>
        )}
    </div>
);

const Input = ({ label, value, onChange, error, placeholder, isWarning }) => (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
        <label className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider ml-1 ${isWarning ? 'text-rose-500' : 'text-gray-500'}`}>
            {label} <span className="text-rose-500">*</span>
        </label>
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1 flex items-center gap-1"><AlertCircle size={12} /> {error}</p>}
    </div>
);

const PreviewCard = ({ icon, label, value, subLabel, subValue, isActive, fullWidth }) => (
    <div className={`flex gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all
        ${isActive ? 'bg-indigo-50/50 border-indigo-200 shadow-sm' : 'bg-white border-gray-200'}`}>
        <div className={`mt-0.5 h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0
            ${isActive ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wider text-gray-400 block mb-0.5">{label}</span>
            <span className={`text-sm sm:text-base font-semibold ${isActive ? 'text-indigo-700' : 'text-gray-700'}`}>{value}</span>
            {isActive && subValue && (
                <div className="mt-2 pt-2 border-t border-indigo-100">
                    <span className="text-[8px] sm:text-[9px] uppercase font-bold text-indigo-400 block">{subLabel}</span>
                    <p className="text-xs sm:text-sm font-medium text-indigo-900 leading-tight mt-0.5 break-words">{subValue}</p>
                </div>
            )}
        </div>
    </div>
);

export default PoliticalForm;